// Policy text for ownpbx.in legal pages. Rendered by pages/LegalPage.jsx.
//
// Written to describe what the OwnPBX platform actually does (call records,
// recordings, AI processing, website forms). If the product changes how data
// is collected or which providers process it, update the matching section.

export const COMPANY = {
  brand: "OwnPBX",
  legalName: "Oneway Networks Technology Pvt. Ltd.",
  email: "ops@ownpbx.in",
  website: "https://ownpbx.in",
};

const UPDATED = "17 September 2026";
const { brand, legalName, email } = COMPANY;

export const LEGAL_NAV = [
  { slug: "privacy", t: "Privacy Policy", to: "/privacy" },
  { slug: "terms", t: "Terms of Service", to: "/terms" },
  { slug: "refund", t: "Refund & Cancellation", to: "/refund-policy" },
  { slug: "delivery", t: "Service Delivery", to: "/service-delivery" },
  { slug: "contact", t: "Contact Us", to: "/contact" },
];

export const LEGAL_PAGES = {
  // ───────────────────────────────────────────────────────────────────────────
  privacy: {
    title: "Privacy Policy",
    updated: UPDATED,
    intro: `This policy explains what personal data ${brand} collects, why, who it is shared with, and the choices you have. ${brand} is operated by ${legalName} ("we", "us").`,
    sections: [
      {
        h: "1. Who this policy covers",
        p: [
          `It applies to visitors of ownpbx.in, people who book a demo or try our live demo, and businesses that sign up for ${brand} ("customers") and their users.`,
          `When a customer uses ${brand} to make or receive calls, the customer decides what calls are recorded, which AI agents are used and how long data is kept. For that call content we process data on the customer's behalf and under their instructions. The customer is responsible for having a lawful basis to do so and for informing the people they call — for example, telling callers that a call may be recorded or handled by an AI agent.`,
        ],
      },
      {
        h: "2. Information we collect",
        list: [
          "Account and contact details: name, business name, email, phone number, and login credentials (passwords are stored only in hashed form).",
          "Billing details: plan, invoices, wallet balance and payment status. Card, UPI and bank details are entered directly with our payment processor; we do not see or store them.",
          "Call detail records: calling and called numbers, time, duration, call outcome, the number or DID used, and the cost of the call.",
          "Call content, only where the customer enables it: call recordings, voicemail, and conversation transcripts.",
          "AI agent data: audio sent to speech recognition, the resulting transcripts, and the agent's replies, plus usage figures such as audio seconds and characters processed.",
          "Technical data: IP addresses, device and browser type, SIP registration and connection details, and logs used to run and secure the service.",
          "Website measurement: which pages were viewed on our website, the site that referred you, and your approximate country, device type and browser. Our website analytics does not use cookies and does not store anything that identifies you personally.",
          "Website forms and live demo: details you type into our demo booking form or live demo (name, email, phone, company, message, preferred date), your conversation with the demo agent, the page you were on and your IP address.",
        ],
      },
      {
        h: "3. How we use it",
        list: [
          "To provide the service: routing and connecting calls, running AI agents, storing recordings, and showing reports.",
          "To bill accurately: rating calls, deducting usage from prepaid balances and issuing invoices.",
          "To keep the platform secure: detecting fraud, blocking abusive traffic and brute-force login attempts, and investigating misuse.",
          "To respond to demo requests and support queries, and to send service and account emails such as welcome, billing and security notices.",
          "To meet legal obligations, including tax records and lawful requests from authorities.",
        ],
        after: ["We do not sell personal data, and we do not use customers' call recordings or transcripts to advertise to anyone."],
      },
      {
        h: "4. AI processing",
        p: [
          `${brand}'s AI features rely on specialised third-party providers for speech recognition, language understanding and voice generation. Depending on the features and voice a customer selects, audio and text from a call or from our website demo may be processed by providers such as Sarvam AI, OpenAI, Deepgram and ElevenLabs.`,
          "These providers receive only what is needed to perform the task in real time. Their own terms govern how they handle that data.",
        ],
      },
      {
        h: "5. Who we share data with",
        list: [
          "Telecom carriers and network partners, to connect calls to and from the public telephone network.",
          "AI service providers, as described above.",
          "Our payment processor, to take payments and issue refunds.",
          "Hosting, email and infrastructure providers that run the service for us.",
          "Our website analytics provider, Plausible Analytics, which receives page-level measurement from our website and no personal data.",
          "Our advertising partner, where you reached our website from one of our ads, so that the ad can be credited. This applies to our website only, not to the customer portal or to anything inside your account.",
          "Authorities, where required by law, court order or a lawful request from a government agency, including under Indian telecommunications law.",
          "A buyer or successor, if our business is merged or acquired, under the same protections described here.",
        ],
      },
      {
        h: "6. Where data is stored",
        p: ["Our servers and some of our service providers may be located outside India. Where data is transferred abroad we take reasonable steps to keep it protected as this policy describes, and as permitted under applicable Indian law."],
      },
      {
        h: "7. How long we keep it",
        p: [
          "Account and billing records are kept while the account is active and afterwards for as long as tax and accounting law requires.",
          "Call detail records, recordings and transcripts are kept according to the customer's settings and plan, and deleted or anonymised when they are no longer needed, unless the law requires us to keep them longer.",
          "Demo requests and live demo conversations are kept for as long as needed to follow up with you, and deleted on request.",
        ],
      },
      {
        h: "8. Security",
        p: ["We use access controls, encrypted connections for the web portal and browser softphone, hashed passwords, rate limiting and automatic blocking of repeated failed login attempts. No system is perfectly secure, and customers are responsible for keeping their passwords and SIP credentials confidential."],
      },
      {
        h: "9. Your rights",
        p: ["Subject to the Digital Personal Data Protection Act, 2023 and other applicable law, you can ask us to:"],
        list: [
          "access the personal data we hold about you;",
          "correct or update it;",
          "erase it, where we are not required to keep it;",
          "withdraw consent you have given, for the future; and",
          "nominate another person to exercise these rights on your behalf.",
        ],
        after: [
          `Email ${email}. If your data was collected by one of our customers (for example, you received a call from a business using ${brand}), please contact that business first; we will help them respond.`,
        ],
      },
      {
        h: "10. Cookies and similar technologies",
        p: [
          "Our website uses browser storage to keep things working — for example, remembering your live demo conversation if you close and reopen it.",
          "For website analytics we use Plausible Analytics, which counts page views without cookies and without collecting or storing any personal data. Because it cannot identify you, there is nothing to consent to and we do not show a cookie banner.",
          "Our public website also carries an advertising measurement pixel, so that a visit which began with one of our ads can be credited to it. It is on the marketing website only. It is not present in the customer portal, and we do not use advertising trackers anywhere inside your account or alongside your calls, recordings or transcripts.",
          "You can block either of these with any standard browser setting, extension or ad blocker. Nothing on this website requires them, and blocking them does not affect your use of the site or the service.",
        ],
      },
      {
        h: "11. Children",
        p: [`${brand} is a business service and is not directed to anyone under 18. We do not knowingly collect children's personal data.`],
      },
      {
        h: "12. Grievances",
        p: [`For any concern about how your personal data is handled, contact our Grievance Officer at ${email}. We aim to acknowledge complaints within 48 hours and resolve them within 30 days.`],
      },
      {
        h: "13. Changes",
        p: ["We may update this policy. The date at the top shows the latest version; significant changes will be communicated to customers by email or in the portal."],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  terms: {
    title: "Terms of Service",
    updated: UPDATED,
    intro: `These terms govern your use of ${brand}, a cloud phone system and AI voice agent platform operated by ${legalName}. By creating an account or using the service you agree to them on behalf of your business.`,
    sections: [
      {
        h: "1. The service",
        p: [`${brand} provides cloud PBX features (extensions, IVR, queues, call recording, reporting), connectivity to the public telephone network through our carrier partners or your own carrier ("bring your own carrier"), and AI voice agents that can answer and place calls. Features depend on the plan you choose.`],
      },
      {
        h: "2. Your account",
        list: [
          "You must be at least 18 and authorised to act for the business you register.",
          "Provide accurate information, including any identity or business documents required to allocate phone numbers under Indian telecom rules.",
          "Keep passwords, API keys and SIP credentials confidential. You are responsible for all activity and charges on your account, including calls made with compromised credentials.",
        ],
      },
      {
        h: "3. Acceptable use",
        p: ["You must not use the service to:"],
        list: [
          "make unsolicited commercial calls in breach of the TRAI Telecom Commercial Communications Customer Preference Regulations, including calling numbers registered on the National Customer Preference Register without a valid basis;",
          "spoof or misrepresent caller ID, or impersonate any person or organisation;",
          "carry out fraud, scams, harassment or threats, or any unlawful activity;",
          "record calls or deploy AI agents without the notice and consent the law requires;",
          "generate artificial traffic, bypass billing, or resell the service without a reseller agreement; or",
          "attack, probe or overload the platform or other customers' accounts.",
        ],
        after: ["We may suspend accounts or block traffic immediately where we reasonably suspect misuse, and report it to authorities where required."],
      },
      {
        h: "4. Emergency calls",
        p: [`${brand} does not support calls to emergency services, including 112, 100, 101, 102 and 108. Keep an alternative way to reach emergency services at all times.`],
      },
      {
        h: "5. AI agents",
        p: [
          "AI agents generate responses automatically and can make mistakes. You are responsible for the instructions and knowledge you give them, for reviewing how they perform, and for the commitments made to your callers.",
          "Do not use AI agents to give medical, legal or financial advice that requires a qualified professional, or to make claims about products that you could not lawfully make yourself.",
        ],
      },
      {
        h: "6. Fees and payment",
        list: [
          "Subscription fees are billed in advance for each billing period. Call usage is charged at the rates for your plan and, on prepaid plans, deducted from your wallet balance as calls are made.",
          "Prices are shown exclusive of GST unless stated otherwise; applicable taxes are added to invoices.",
          "If your balance runs out or a payment fails, outgoing calls or the account may be restricted until it is settled.",
          "We may change prices with at least 30 days' notice. Changes apply from your next billing period.",
        ],
        after: ["Refunds and cancellations are covered by our Refund & Cancellation Policy."],
      },
      {
        h: "7. Phone numbers",
        p: ["Numbers allocated to you remain subject to carrier and regulatory rules. They may need to be withdrawn if required by a carrier or regulator, or if your account is closed or unpaid. Number porting is subject to carrier approval."],
      },
      {
        h: "8. Availability",
        p: [
          "We work to keep the service running reliably, but it depends on internet connectivity and third-party carriers and providers outside our control. The service is provided on an \"as available\" basis without a guaranteed uptime, unless a separate written service level agreement applies.",
          "We may carry out maintenance, preferably at low-traffic times, and will give notice of planned downtime where practical.",
        ],
      },
      {
        h: "9. Your data",
        p: [`You own your call recordings, transcripts, contacts and configuration. You permit us to process them only to provide, secure and support the service, as described in our Privacy Policy. You can export your data before closing your account.`],
      },
      {
        h: "10. Suspension and termination",
        p: ["You can cancel at any time as described in the Refund & Cancellation Policy. We may suspend or terminate the service for non-payment, breach of these terms or misuse, or where required by law. On termination, access ends and data is deleted after a reasonable period unless the law requires us to keep it."],
      },
      {
        h: "11. Limitation of liability",
        p: [
          "To the extent permitted by law, we are not liable for indirect or consequential losses, lost profits or revenue, missed calls, or losses caused by carriers, AI providers or events beyond our reasonable control.",
          "Our total liability for any claim relating to the service is limited to the fees you paid us in the three months before the event giving rise to the claim.",
        ],
      },
      {
        h: "12. Indemnity",
        p: ["You will compensate us for claims, penalties and costs arising from your breach of these terms or from your unlawful use of the service, including your calls and your AI agents' conversations."],
      },
      {
        h: "13. Governing law",
        p: [`These terms are governed by the laws of India. Courts having jurisdiction over the registered office of ${legalName} have exclusive jurisdiction over any dispute.`],
      },
      {
        h: "14. Trademarks",
        p: [`${brand}™ and the ${brand} logo are trademarks of ${legalName}. You may not use them without our written permission, except to refer to the service as it is.`],
      },
      {
        h: "15. Changes and contact",
        p: [`We may update these terms and will notify customers of material changes in advance. Questions: ${email}.`],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  refund: {
    title: "Refund & Cancellation Policy",
    updated: UPDATED,
    intro: `How cancellations work, and when you can get money back for ${brand} subscriptions and prepaid balance.`,
    sections: [
      {
        h: "1. Cancelling your subscription",
        p: [
          `You can cancel at any time by writing to ${email} from your registered email address. Cancellation takes effect at the end of your current billing period, and the service stays available until then.`,
          "There is no charge for cancelling.",
        ],
      },
      {
        h: "2. Subscription fees",
        p: ["Subscription fees are paid in advance and are not refunded for a billing period that has already started, including partially used periods, except in the cases listed in section 4."],
      },
      {
        h: "3. Prepaid balance and usage",
        list: [
          "Wallet balance is used to pay for calls and usage and is not transferable to another account.",
          "Charges for calls and other usage that has already taken place are not refundable.",
          "Unused wallet balance is not refunded when an account is closed, except in the cases listed in section 4.",
          "One-time charges such as phone number setup or porting are not refundable once the work has been carried out.",
        ],
      },
      {
        h: "4. When we do refund",
        list: [
          "You were charged more than once for the same order.",
          "Your payment was deducted but the order failed and the service was never activated.",
          "You were billed incorrectly because of an error on our side.",
          "We are unable to activate a service you paid for.",
        ],
        after: [`Raise a refund request with ${email} within 7 days of the charge, including your registered email and the payment reference.`],
      },
      {
        h: "5. How refunds are paid",
        p: ["Approved refunds are made to the original payment method within 7–10 business days of approval. The time for the amount to reflect in your account depends on your bank or payment provider."],
      },
      {
        h: "6. Free trials",
        p: ["Free trials are not charged. If a trial requires payment details, you will not be charged unless you choose a paid plan."],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  delivery: {
    title: "Service Delivery Policy",
    updated: UPDATED,
    intro: `${brand} is a software service delivered online. Nothing is physically shipped.`,
    sections: [
      {
        h: "1. Account activation",
        p: ["Self-service signups are activated automatically once payment is confirmed. Login details are sent to the email address used at signup, usually within a few minutes."],
      },
      {
        h: "2. Phone numbers and custom setups",
        p: ["Phone numbers that require documentation, number porting, carrier configuration or AI agent setup can take longer. We will tell you the expected time when you order, typically between 1 and 7 business days depending on carrier and regulatory checks."],
      },
      {
        h: "3. If activation is delayed",
        p: [`If you have paid and have not received your login details within 24 hours, email ${email} with your payment reference. If we cannot activate the service, you are entitled to a refund as described in our Refund & Cancellation Policy.`],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  contact: {
    title: "Contact Us",
    updated: UPDATED,
    intro: "Questions about sales, support, billing or your data — we're happy to help.",
    sections: [
      {
        h: "Support and sales",
        p: [
          `Email: ${email}`,
          "We respond to support requests within one business day, and faster for issues affecting live calls.",
        ],
      },
      {
        h: "Book a demo",
        p: ["See OwnPBX live, tailored to your business — use the Book a Demo form on this website."],
      },
      {
        h: "Billing, refunds and privacy requests",
        p: [`Email ${email} from your registered email address with your account details. Privacy and grievance requests are handled as described in our Privacy Policy.`],
      },
      {
        h: "Company",
        p: [`${brand} is operated by ${legalName}, India.`],
      },
    ],
  },
};
